/**
 * Error page - generic error and 404 states
 */
import { BaseElement } from '../base-element';
import { navigate } from '../../router';

export class ErrorPage extends BaseElement {
  connectedCallback(): void {
    this.updateRender();
  }

  private updateRender(): void {
    const code = this.getAttribute('code');
    const content = code === '404' ? this.renderNotFound() : this.renderGeneric();
    this.render(content);
    this.attachEventListeners();
  }

  private renderNotFound(): string {
    return `
      <div class="flex min-h-[400px] flex-col items-center justify-center px-4 py-12 text-center" data-testid="error-404">
        <div class="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary-50 text-primary-500 dark:bg-neutral-800">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-10 w-10">
            <path fill-rule="evenodd" d="M4.5 6.75a1.5 1.5 0 000 3h1.5v4.5H4.5a1.5 1.5 0 000 3H9a1.5 1.5 0 000-3H7.5v-4.5H9a1.5 1.5 0 000-3H4.5zm10.5 0a1.5 1.5 0 000 3h1.5v4.5H15a1.5 1.5 0 000 3h4.5a1.5 1.5 0 000-3H18v-4.5h1.5a1.5 1.5 0 000-3H15z" clip-rule="evenodd"/>
          </svg>
        </div>
        <h1 class="mb-2 text-2xl font-bold text-neutral-900 dark:text-white">Page Not Found</h1>
        <p class="mb-6 text-sm text-neutral-600 dark:text-neutral-400">We couldn't find the page you're looking for.</p>
        <button class="inline-flex items-center justify-center rounded-xl bg-primary-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600" data-testid="go-home-btn">Go to Home</button>
      </div>
    `;
  }

  private renderGeneric(): string {
    const params = new URLSearchParams(window.location.search);
    const supportId = params.get('supportId');
    const message = params.get('message') || 'An unexpected error occurred.';

    return `
      <div class="flex min-h-[400px] flex-col items-center justify-center px-4 py-12 text-center" data-testid="error-generic">
        <div class="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-error-50 text-error-500 dark:bg-error-500/10">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-10 w-10">
            <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
          </svg>
        </div>
        <h1 class="mb-2 text-2xl font-bold text-neutral-900 dark:text-white">Something Went Wrong</h1>
        <p class="mb-4 text-sm text-neutral-600 dark:text-neutral-400" data-testid="error-message">${this.esc(message)}</p>
        ${supportId ? `<p class="mb-6 text-xs text-neutral-500 dark:text-neutral-400" data-testid="support-id">Support ID: ${this.esc(supportId!)}</p>` : '<div class="mb-6"></div>'}
        <div class="flex flex-wrap items-center justify-center gap-3">
          <button class="inline-flex items-center justify-center rounded-xl border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800" data-testid="try-again-btn">Try Again</button>
          <button class="inline-flex items-center justify-center rounded-xl bg-primary-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600" data-testid="go-home-btn">Go to Home</button>
        </div>
      </div>
    `;
  }

  private attachEventListeners(): void {
    const goHomeBtn = this.$('[data-testid="go-home-btn"]');
    if (goHomeBtn) {
      goHomeBtn.addEventListener('click', () => navigate('/trials'));
    }

    const tryAgainBtn = this.$('[data-testid="try-again-btn"]');
    if (tryAgainBtn) {
      tryAgainBtn.addEventListener('click', () => window.history.back());
    }
  }
}

customElements.define('dt-error-page', ErrorPage);
